import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useRouter } from "../../router";
import { agentApi, type AgentEvent, type AgentThread, type Item, type StreamEvent } from "./agentApi";
import "./agent.css";

// One conversation with Anders' assistant (M15): the transcript with streaming
// answers, a compact row per tool call, approval cards for draft writes, the
// running cost, and the hand-off of a prepared advice to the Advisory workbench.
// Used by the drawer on the workbench and by the Assistant area.

type LocalItem = Item | { kind: "notice"; text: string; tone: "info" | "error" };

const TOOL_LABELS: Record<string, string> = {
  flow_create_instance: "Create an advice draft",
  flow_update_instance: "Save advice information",
  flow_save_component: "Save a step",
  flow_update_goal: "Update the goal",
  flow_create_goal: "Add a goal",
  flow_set_goal_platform: "Set the goal's platform",
  flow_create_portfolio: "Add a portfolio",
  flow_update_portfolio: "Update the portfolio",
  flow_add_portfolio_product: "Add a product",
  flow_update_portfolio_product: "Change a product amount",
  product_add_to_portfolio: "Add a product",
  product_update_in_portfolio: "Change a product amount",
  flow_regenerate_snapshots: "Regenerate analytics",
  wealth_plan_add: "Add a planned withdrawal",
  wealth_plan_update: "Change a planned withdrawal",
  note_create: "Write a client note",
  client_create: "Create the client in Quantfolio",
  log_activity: "Log a CRM activity",
};

function bare(tool: string): string {
  return tool.replace(/^(qp|andersen)_/, "");
}

export function toolLabel(tool: string): string {
  const b = bare(tool);
  return TOOL_LABELS[b] ?? b.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function money(n: number): string {
  return `$${n < 0.01 && n > 0 ? n.toFixed(4) : n.toFixed(2)}`;
}

function kTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

// ---- a very small, safe markdown subset (no HTML injection: React elements only) ----

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    out.push(
      tok.startsWith("**") ? <strong key={`${key}-${i++}`}>{tok.slice(2, -2)}</strong> : <code key={`${key}-${i++}`}>{tok.slice(1, -1)}</code>,
    );
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r/g, "").split("\n");
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) blocks.push(<p key={`p${blocks.length}`}>{inline(para.join(" "), `p${blocks.length}`)}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((it, i) => <li key={i}>{inline(it, `li${blocks.length}-${i}`)}</li>);
    blocks.push(list.ordered ? <ol key={`l${blocks.length}`}>{items}</ol> : <ul key={`l${blocks.length}`}>{items}</ul>);
    list = null;
  };
  let table: string[][] | null = null;
  const flushTable = () => {
    if (!table) return;
    const [head, ...body] = table;
    blocks.push(
      <div key={`t${blocks.length}`} className="agent-table-wrap">
        <table className="agent-table">
          <thead>
            <tr>{head!.map((c, i) => <th key={i}>{inline(c, `th${blocks.length}-${i}`)}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri}>{r.map((c, i) => <td key={i}>{inline(c, `td${blocks.length}-${ri}-${i}`)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    table = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flushPara();
      flushList();
      const cells = line.trim().slice(1, -1).split("|").map((c) => c.trim());
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue; // the |---| separator
      (table ??= []).push(cells);
      continue;
    }
    flushTable();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const num = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const head = line.match(/^#{1,4}\s+(.*)$/);
    if (bullet || num) {
      flushPara();
      const ordered = Boolean(num);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet ?? num)![1]!);
    } else if (head) {
      flushPara();
      flushList();
      blocks.push(<h4 key={`h${blocks.length}`}>{inline(head[1]!, `h${blocks.length}`)}</h4>);
    } else if (!line.trim()) {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  flushTable();
  return <div className="agent-md">{blocks}</div>;
}

// ---- tool rows and approval cards ------------------------------------------------------

function StatusMark({ status }: { status: AgentEvent["status"] }) {
  if (status === "ok") return <span className="agent-mark ok" aria-label="done">✓</span>;
  if (status === "error") return <span className="agent-mark err" aria-label="failed">✗</span>;
  if (status === "declined") return <span className="agent-mark err" aria-label="declined">–</span>;
  if (status === "pending_approval") return <span className="agent-mark wait">awaiting you</span>;
  return <span className="agent-spinner" aria-label="running" />;
}

function ToolRow({ ev }: { ev: AgentEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`agent-tool ${ev.tool_class === "draft" ? "is-draft" : ""}`}>
      <button type="button" className="agent-tool-row" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={`agent-src agent-src-${ev.source}`}>{ev.source === "quantfolio" ? "QP" : "AW"}</span>
        <span className="agent-tool-name">{toolLabel(ev.tool)}</span>
        <span className="agent-tool-target">{ev.target ?? ""}</span>
        <StatusMark status={ev.status} />
      </button>
      {open && (
        <div className="agent-tool-detail">
          <div>
            <span className="label">Tool</span> {bare(ev.tool)} · {ev.source === "quantfolio" ? "Quantfolio MCP" : "Andersen MCP"}
            {ev.duration_ms != null && ` · ${ev.duration_ms} ms`}
          </div>
          {ev.approved_by && (
            <div>
              <span className="label">Approved</span> {ev.approved_by}
            </div>
          )}
          {ev.result_summary && <div className="agent-tool-summary">{ev.result_summary}</div>}
        </div>
      )}
    </div>
  );
}

function ArgsPreview({ args }: { args: unknown }) {
  const entries = args && typeof args === "object" ? Object.entries(args as Record<string, unknown>) : [];
  return (
    <dl className="agent-args">
      {entries.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{typeof v === "string" ? v : JSON.stringify(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ApprovalCard({
  ev,
  allowDrafts,
  busy,
  onDecide,
}: {
  ev: AgentEvent;
  allowDrafts: boolean;
  busy: boolean;
  onDecide: (d: { decision: "approve" | "decline"; args?: Record<string, unknown>; allowDrafts?: boolean }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => JSON.stringify(ev.args ?? {}, null, 2));
  const [err, setErr] = useState<string | null>(null);
  const [allow, setAllow] = useState(allowDrafts);

  const approve = () => {
    if (!editing) return onDecide({ decision: "approve", allowDrafts: allow });
    try {
      const parsed = JSON.parse(draft) as unknown;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Must be a JSON object.");
      onDecide({ decision: "approve", args: parsed as Record<string, unknown>, allowDrafts: allow });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Invalid JSON.");
    }
  };

  return (
    <div className="agent-approval" role="group" aria-label="Approval needed">
      <div className="agent-approval-head">
        <span className="eyebrow">Needs your approval</span>
        <span className={`agent-src agent-src-${ev.source}`}>{ev.source === "quantfolio" ? "QP" : "AW"}</span>
      </div>
      <p className="agent-approval-title">
        The assistant wants to: <strong>{toolLabel(ev.tool)}</strong>
        {ev.target && <span className="muted"> — {ev.target}</span>}
      </p>
      {editing ? (
        <textarea
          className="agent-approval-edit"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setErr(null);
          }}
          spellCheck={false}
          rows={Math.min(14, draft.split("\n").length + 1)}
        />
      ) : (
        <ArgsPreview args={ev.args} />
      )}
      {err && <p className="agent-error">{err}</p>}
      <label className="agent-allow">
        <input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} />
        Allow drafts for this advice (no more cards for draft saves)
      </label>
      <div className="agent-approval-actions">
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={approve}>
          {editing ? "Approve edited" : "Approve"}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setEditing((e) => !e)}>
          {editing ? "Cancel edit" : "Edit"}
        </button>
        <button type="button" className="btn btn-ghost btn-sm agent-decline" disabled={busy} onClick={() => onDecide({ decision: "decline" })}>
          Decline
        </button>
      </div>
    </div>
  );
}

// ---- the conversation --------------------------------------------------------------------

function blankEvent(e: Extract<StreamEvent, { type: "tool-call" }>): AgentEvent {
  return {
    id: 0,
    tool_call_id: e.toolCallId,
    approval_id: null,
    tool: e.tool,
    source: e.source === "quantfolio" ? "quantfolio" : "andersen",
    tool_class: e.cls === "draft" ? "draft" : "read",
    target: e.target,
    args: e.args,
    edited_args: null,
    status: "running",
    result_summary: null,
    approved_by: null,
    actor: "",
    duration_ms: null,
    created_at: new Date().toISOString(),
  };
}

export function AgentChat({
  threadId,
  onThread,
  placeholder,
  suggestions,
  currentSessionId,
}: {
  threadId: number;
  onThread?: (t: AgentThread) => void;
  placeholder?: string;
  suggestions?: string[];
  /** The workbench session on screen (drawer): no hand-off button for its own advice. */
  currentSessionId?: number;
}) {
  const { navigate } = useRouter();
  const [thread, setThread] = useState<AgentThread | null>(null);
  const [items, setItems] = useState<LocalItem[]>([]);
  const [cap, setCap] = useState(0);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const scroller = useRef<HTMLDivElement | null>(null);
  const onThreadRef = useRef(onThread);
  onThreadRef.current = onThread;

  const applyThread = useCallback((t: AgentThread) => {
    setThread(t);
    onThreadRef.current?.(t);
  }, []);

  const reload = useCallback(async () => {
    try {
      const d = await agentApi.get(threadId);
      applyThread(d.thread);
      setItems(d.items);
      setCap(d.maxCostPerThread);
      setBusy(d.busy);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : "Could not load the conversation.");
    }
  }, [threadId, applyThread]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items]);

  const onEvent = useCallback(
    (e: StreamEvent) => {
      setItems((prev) => {
        const next = [...prev];
        const findTool = (id: string) =>
          next.findIndex((it) => it.kind === "tool" && it.event.tool_call_id === id);
        const patchTool = (id: string, patch: Partial<AgentEvent>) => {
          const i = findTool(id);
          if (i >= 0) {
            const it = next[i] as Extract<LocalItem, { kind: "tool" }>;
            next[i] = { kind: "tool", event: { ...it.event, ...patch } };
          }
        };
        switch (e.type) {
          case "text": {
            const last = next[next.length - 1];
            if (last && last.kind === "assistant" && last.at === "streaming") {
              next[next.length - 1] = { ...last, text: last.text + e.delta };
            } else {
              next.push({ kind: "assistant", text: e.delta, at: "streaming", model: null, cost: null });
            }
            break;
          }
          case "tool-call":
            if (findTool(e.toolCallId) < 0) next.push({ kind: "tool", event: blankEvent(e) });
            break;
          case "approval":
            patchTool(e.toolCallId, { status: "pending_approval", approval_id: e.approvalId });
            break;
          case "tool-result":
            patchTool(e.toolCallId, { status: e.ok ? "ok" : "error", result_summary: e.summary, duration_ms: e.durationMs });
            break;
          case "denied":
            patchTool(e.toolCallId, { status: "declined", result_summary: e.reason });
            break;
          case "notice":
            next.push({ kind: "notice", text: e.message, tone: "info" });
            break;
          case "error":
            next.push({ kind: "notice", text: e.message, tone: "error" });
            break;
          default:
            break;
        }
        return next;
      });
      if (e.type === "thread") applyThread(e.thread);
      if (e.type === "usage") setCap(e.cap);
    },
    [applyThread],
  );

  const run = useCallback(
    async (fn: () => Promise<{ streamed: boolean }>) => {
      setBusy(true);
      try {
        const r = await fn();
        if (r.streamed) await reload();
      } catch (e) {
        setItems((prev) => [...prev, { kind: "notice", text: e instanceof Error ? e.message : "Request failed.", tone: "error" }]);
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const send = (msg?: string) => {
    const t = (msg ?? text).trim();
    if (!t || busy) return;
    setText("");
    setItems((prev) => [...prev, { kind: "user", text: t, at: new Date().toISOString() }]);
    void run(() => agentApi.send(threadId, t, onEvent));
  };

  const decide = (ev: AgentEvent, d: { decision: "approve" | "decline"; args?: Record<string, unknown>; allowDrafts?: boolean }) => {
    if (!ev.approval_id) return;
    const approvalId = ev.approval_id;
    setItems((prev) =>
      prev.map((it) =>
        it.kind === "tool" && it.event.tool_call_id === ev.tool_call_id
          ? { kind: "tool", event: { ...it.event, status: d.decision === "approve" ? "running" : "declined" } }
          : it,
      ),
    );
    void run(async () => {
      const r = await agentApi.decide(threadId, approvalId, d, onEvent);
      if (!r.streamed) await reload();
      return r;
    });
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // A5: hand the prepared advice to the workbench (adopt), link the thread, open it.
  const openInAdvisory = async () => {
    if (!thread?.qp_flow_instance_id) return;
    if (thread.advisory_session_id) return navigate(`/app/advisory/${thread.advisory_session_id}`);
    if (!thread.investor_id) {
      setHandoff({ busy: false, error: "The advice is not tied to an Andersen investor yet." });
      return;
    }
    setHandoff({ busy: true, error: null });
    try {
      const { session } = await agentApi.adopt(thread.investor_id, thread.qp_flow_instance_id);
      const { thread: t } = await agentApi.patch(thread.id, { advisorySessionId: session.id });
      applyThread(t);
      setHandoff({ busy: false, error: null });
      navigate(`/app/advisory/${session.id}`);
    } catch (e) {
      setHandoff({ busy: false, error: e instanceof Error ? e.message : "Could not open the advice." });
    }
  };

  const toggleDrafts = async (on: boolean) => {
    if (!thread) return;
    try {
      applyThread((await agentApi.patch(thread.id, { allowDrafts: on })).thread);
    } catch {
      /* keep the old state */
    }
  };

  if (loadErr) return <p className="agent-error agent-pad">{loadErr}</p>;

  const pct = cap > 0 && thread ? Math.min(100, (thread.cost / cap) * 100) : 0;
  const capped = Boolean(thread && cap > 0 && thread.cost >= cap);

  return (
    <div className="agent-chat">
      <div className="agent-scroll" ref={scroller}>
        {items.length === 0 && (
          <div className="agent-empty">
            <p className="muted">
              Ask about a client's holdings and advice, or ask me to prepare an Investment Advice. I read Andersen's
              records and work in Quantfolio through its MCP tools. Every write waits for your approval; I never submit.
            </p>
            {suggestions && suggestions.length > 0 && (
              <div className="agent-suggestions">
                {suggestions.map((s) => (
                  <button key={s} type="button" className="agent-suggestion" onClick={() => send(s)} disabled={busy}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {items.map((it, i) => {
          if (it.kind === "user") {
            return (
              <div key={i} className="agent-msg agent-user">
                {it.text}
              </div>
            );
          }
          if (it.kind === "assistant") {
            return (
              <div key={i} className="agent-msg agent-assistant">
                <Markdown text={it.text} />
              </div>
            );
          }
          if (it.kind === "notice") {
            return (
              <p key={i} className={it.tone === "error" ? "agent-error" : "agent-notice"}>
                {it.text}
              </p>
            );
          }
          const ev = it.event;
          if (ev.status === "pending_approval") {
            return (
              <ApprovalCard
                key={ev.tool_call_id}
                ev={ev}
                allowDrafts={thread?.allow_drafts === 1}
                busy={busy}
                onDecide={(d) => decide(ev, d)}
              />
            );
          }
          return <ToolRow key={ev.tool_call_id} ev={ev} />;
        })}
        {busy && !items.some((it) => it.kind === "tool" && it.event.status === "pending_approval") && (
          <div className="agent-thinking">
            <span className="agent-spinner" /> Working…
          </div>
        )}
      </div>

      {thread?.qp_flow_instance_id && (
        <div className="agent-handoff">
          <div>
            <span className="label">Advice prepared in Quantfolio</span>
            <span className="agent-handoff-id">{thread.qp_flow_instance_id.slice(0, 8)}…</span>
          </div>
          {currentSessionId && thread.advisory_session_id === currentSessionId ? (
            <span className="muted">Open in this workbench · advice #{currentSessionId}</span>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" onClick={openInAdvisory} disabled={handoff.busy || busy}>
              {handoff.busy ? "Opening…" : "Open in Advisory"}
            </button>
          )}
          {handoff.error && <p className="agent-error">{handoff.error}</p>}
        </div>
      )}

      <div className="agent-compose">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder={capped ? "This conversation reached its cost cap." : (placeholder ?? "Ask Anders' assistant…")}
          rows={2}
          disabled={capped}
          maxLength={8000}
          aria-label="Message"
        />
        <button type="button" className="btn btn-primary btn-sm" onClick={() => send()} disabled={busy || capped || !text.trim()}>
          Send
        </button>
      </div>

      {thread && (
        <div className="agent-meta">
          <label className="agent-allow">
            <input
              type="checkbox"
              checked={thread.allow_drafts === 1}
              onChange={(e) => void toggleDrafts(e.target.checked)}
            />
            Allow drafts for this advice
          </label>
          <span className="agent-cost" title={`${thread.tokens_in} tokens in, ${thread.tokens_out} tokens out · ${thread.provider}/${thread.model}`}>
            <span className="agent-cost-bar" aria-hidden>
              <span style={{ width: `${pct}%` }} />
            </span>
            {money(thread.cost)} of {money(cap)} · {kTokens(thread.tokens_in + thread.tokens_out)} tokens
          </span>
        </div>
      )}
    </div>
  );
}
